import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import FormRenderer from '../shared/FormRenderer';
import SuccessPopup from '../shared/SuccessPopup';
import { formAppliesToGrade } from '../../utils/formApplicability';
import { confirmUngeocodedMapAddressStandaloneIfNeeded } from '../../utils/mapSubmitGuard';
import { getClientMetadata } from '../../utils/signatureMetadata';

const EditStudentForm = () => {
  const navigate = useNavigate();
  const { studentIntakeId } = useParams();

  // Student and form list state
  const [studentIntake, setStudentIntake] = useState(null);
  const [submittedForms, setSubmittedForms] = useState([]);
  const [availableForms, setAvailableForms] = useState([]);

  // Individual form editing state
  const [selectedForm, setSelectedForm] = useState(null);
  const [currentFormDefinition, setCurrentFormDefinition] = useState(null);
  const [currentFormData, setCurrentFormData] = useState({});
  const [currentSubmissionId, setCurrentSubmissionId] = useState(null);

  // Modal state for adding new forms
  const [showAddFormModal, setShowAddFormModal] = useState(false);
  const [modalFormInstance, setModalFormInstance] = useState(null);
  const [modalFormDefinition, setModalFormDefinition] = useState(null);
  const [modalFormData, setModalFormData] = useState({});
  const [savingModal, setSavingModal] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [parentEditsLocked, setParentEditsLocked] = useState(false);

  const parentEditsLockedNote =
    'Form edits are currently locked by the district for this school year. Please contact the district if you need to make changes.';

  // Load student info and submitted forms
  useEffect(() => {
    const loadStudentData = async () => {
      if (!studentIntakeId) return;

      try {
        setLoading(true);

        // Load student intake info
        const studentResponse = await axios.get('/api/student-intake/my-intake');
        const students = studentResponse.data;
        const student = students.find(s => s.id === studentIntakeId);
        if (student) {
          setStudentIntake(student);
        }

        // Load submitted forms for this student
        const formsResponse = await axios.get(`/api/student-intake/${studentIntakeId}/forms`);
        // Filter out helper forms - they should only exist in new student context
        const filteredForms = (formsResponse.data || []).filter(form => {
          const formName = (form.instance_name || form.master_form_name || '').toLowerCase();
          return !formName.includes('helper') && !formName.includes('information helper');
        });
        setSubmittedForms(filteredForms);

        // Load all available forms for potential addition
        const availableResponse = await axios.get('/api/form-instances/parent');
        
        /**
         * What this child can still be given, one form at a time.
         *
         * Was a pile of substring tests against the instance name AND the master form name
         * concatenated. That combination is what broke it: Van Buren renamed their instance to
         * "New Student Form 2026-2027", but the master form is still "New Student Form 2025", so
         * the exclusion for "new student form 2025" kept firing and the New Student Form could
         * never be added. Parents who reached this screen added every side form and left without
         * the one that collects the child's information - 29 records ended up that way.
         *
         * Now: keep any form this child actually owes, by form_type and grade, using the same
         * rule the enrollment packet uses (utils/formApplicability.js). That drops the helper
         * form, the school lunch application and the dead returning-student form on their type,
         * and it stops offering Early Dismissal to a 7-12 student and Chromebook to a
         * preschooler - both of which this screen used to hand out regardless of grade.
         *
         * The New Student Form is deliberately offerable here. The guided packet is the normal
         * route and district staff should expect it, but when a child is missing only that form
         * this screen is the only way a parent can finish without calling the school.
         */
        const filteredAvailableForms = (availableResponse.data || []).filter((form) =>
          formAppliesToGrade(form, student?.grade)
        );

        setAvailableForms(filteredAvailableForms);

        try {
          const districtRes = await axios.get('/api/districts/current');
          setParentEditsLocked(!!districtRes.data?.parent_edits_locked);
        } catch {
          setParentEditsLocked(false);
        }

      } catch (error) {
        console.error('Failed to load student data:', error);
        setError('Failed to load student information');
      } finally {
        setLoading(false);
      }
    };

    loadStudentData();
  }, [studentIntakeId]);

  // Handle form selection for editing
  const selectForm = async (form) => {
    try {
      setLoading(true);
      setSelectedForm(form);

      // Load the form definition
      const instancesResponse = await axios.get('/api/form-instances/parent');
      const allInstances = instancesResponse.data || [];
      const formInstanceData = allInstances.find((inst) => inst.id === form.form_instance_id);

      if (!formInstanceData) {
        setError('Form definition not found');
        return;
      }

      // Create form definition from the instance
      const formDefinition = {
        sections: [{
          section_name: formInstanceData.instance_name || formInstanceData.master_form_name,
          section_note: formInstanceData.instance_description || null,
          fields: []
        }]
      };

      // Load form fields from master form data
      if (formInstanceData.master_form_data) {
        let parsedFormData;
        if (typeof formInstanceData.master_form_data === 'string') {
          try {
            parsedFormData = JSON.parse(formInstanceData.master_form_data);
          } catch (parseError) {
            console.error('Failed to parse master form data:', parseError);
            setError('Form definition is corrupted');
            return;
          }
        } else if (typeof formInstanceData.master_form_data === 'object') {
          parsedFormData = formInstanceData.master_form_data;
        }

        if (parsedFormData && parsedFormData.sections) {
          console.log('Form has', parsedFormData.sections.length, 'sections');

          // For editing existing forms, combine all sections into one (skip admin-only sections for parents)
          const allFields = [];
          parsedFormData.sections.forEach((section, index) => {
            console.log(`Section ${index}: ${section.section_name}, ${section.fields?.length || 0} fields, admin_only: ${section.admin_only}`);
            // Skip admin-only sections for parents
            if (section.admin_only) {
              console.log(`Skipping admin-only section: ${section.section_name}`);
              return;
            }
            if (section.fields) {
              allFields.push(...section.fields);
            }
          });

          formDefinition.sections[0].fields = allFields;
          console.log('Total loaded fields:', allFields.length);
        }
      }

      // Load existing form data
      const existingData = {};
      if (form.form_data && typeof form.form_data === 'object') {
        Object.entries(form.form_data).forEach(([key, value]) => {
          existingData[key] = value;
        });
      }

      setCurrentFormDefinition(formDefinition);
      setCurrentFormData(existingData);
      setCurrentSubmissionId(form.submission_id);

    } catch (error) {
      console.error('Failed to load form:', error);
      setError('Failed to load form for editing');
    } finally {
      setLoading(false);
    }
  };

  // Handle saving edited form changes
  const saveEditedForm = async (formData) => {
    if (!selectedForm || !currentSubmissionId) return;

    try {
      setSaving(true);
      setError('');

      // Capture submission metadata
      const clientMetadata = await getClientMetadata();
      let ipAddress = null;
      try {
        const ipResponse = await axios.get('/api/auth/client-info');
        ipAddress = ipResponse.data.ipAddress || null;
      } catch (error) {
        console.warn('Could not fetch IP address for submission metadata:', error);
      }

      // Enhance form data with submission metadata
      const enhancedFormData = {
        ...formData,
        _submissionMetadata: {
          ...clientMetadata,
          ipAddress,
          submittedAt: new Date().toISOString(),
          submittedBy: 'parent', // Role identifier
          formInstanceId: selectedForm.form_instance_id,
          submissionType: 'edit'
        }
      };

      await axios.put(`/api/form-submissions/update/${currentSubmissionId}`, {
        submission_data: enhancedFormData,
        form_instance_id: selectedForm.form_instance_id
      });

      setSaveFeedback({
        variant: 'success',
        title: 'Saved',
        message: 'Form changes were saved successfully.'
      });

      // Refresh the submitted forms list
      const formsResponse = await axios.get(`/api/student-intake/${studentIntakeId}/forms`);
      setSubmittedForms(formsResponse.data || []);

    } catch (error) {
      console.error('Failed to save form:', error);
      setSaveFeedback({
        variant: 'error',
        title: 'Save failed',
        message: error.response?.data?.error || 'Failed to save form'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle opening modal to add new form
  const openAddFormModal = async (form) => {
    try {
      setLoading(true);

      // Load the form definition for the modal
      console.log('Loading form definition for modal, form:', form);
      const instancesResponse = await axios.get('/api/form-instances/parent');
      console.log('API response:', instancesResponse.data);
      const allInstances = instancesResponse.data || [];
      const formInstanceData = allInstances.find(inst => inst.id === form.id);
      console.log('Found form instance data:', formInstanceData);

      if (!formInstanceData) {
        console.error('Form definition not found for id:', form.id);
        setError('Form definition not found');
        return;
      }

      // Create form definition from the instance
      const formDefinition = {
        sections: [{
          section_name: formInstanceData.instance_name || formInstanceData.master_form_name,
          section_note: formInstanceData.instance_description || null,
          fields: []
        }]
      };

      // Load form fields from master form data
      console.log('formInstanceData.master_form_data:', formInstanceData.master_form_data);
      if (formInstanceData.master_form_data) {
        let parsedFormData;
        if (typeof formInstanceData.master_form_data === 'string') {
          try {
            parsedFormData = JSON.parse(formInstanceData.master_form_data);
            console.log('Parsed master form data:', parsedFormData);
          } catch (parseError) {
            console.error('Failed to parse master form data:', parseError);
            setError('Form definition is corrupted');
            return;
          }
        } else if (typeof formInstanceData.master_form_data === 'object') {
          parsedFormData = formInstanceData.master_form_data;
          console.log('Master form data is already an object:', parsedFormData);
        }

        if (parsedFormData && parsedFormData.sections) {
          console.log('Form has', parsedFormData.sections.length, 'sections');

          // For modal, combine all sections into one (skip admin-only sections for parents)
          const allFields = [];
          parsedFormData.sections.forEach((section, index) => {
            console.log(`Section ${index}: ${section.section_name}, ${section.fields?.length || 0} fields, admin_only: ${section.admin_only}`);
            // Skip admin-only sections for parents
            if (section.admin_only) {
              console.log(`Skipping admin-only section: ${section.section_name}`);
              return;
            }
            if (section.fields) {
              allFields.push(...section.fields);
            }
          });

          formDefinition.sections[0].fields = allFields;
          console.log('Total loaded fields:', allFields.length);
          console.log('Field details:', allFields.slice(0, 5).map(f => ({ name: f.name, type: f.type, label: f.label })));
        } else {
          console.log('No sections found in master form data');
        }
      } else {
        console.log('No master_form_data found');
      }

      console.log('Final form definition for modal:', formDefinition);
      console.log('Number of fields in section 0:', formDefinition.sections[0].fields.length);

      setModalFormInstance(form);
      setModalFormDefinition(formDefinition);
      setModalFormData({});
      setShowAddFormModal(true);

    } catch (error) {
      console.error('Failed to load form for modal:', error);
      setError('Failed to open form');
    } finally {
      setLoading(false);
    }
  };

  // Handle submitting the form from the modal
  const submitModalForm = async () => {
    if (!modalFormInstance || !modalFormDefinition) return;

    // Capture submission metadata
    const clientMetadata = await getClientMetadata();
    let ipAddress = null;
    try {
      const ipResponse = await axios.get('/api/auth/client-info');
      ipAddress = ipResponse.data.ipAddress || null;
    } catch (error) {
      console.warn('Could not fetch IP address for submission metadata:', error);
    }

    // Enhance form data with submission metadata
    const enhancedFormData = {
      ...modalFormData,
      _submissionMetadata: {
        ...clientMetadata,
        ipAddress,
        submittedAt: new Date().toISOString(),
        submittedBy: 'parent', // Role identifier
        formInstanceId: modalFormInstance.id,
        submissionType: 'new'
      }
    };

    if (
      modalFormDefinition &&
      !confirmUngeocodedMapAddressStandaloneIfNeeded(modalFormDefinition, modalFormData, modalFormInstance)
    ) {
      return;
    }

    try {
      setSavingModal(true);
      setError('');
      setSaveFeedback(null);

      console.log('Submitting modal form:', {
        form: modalFormInstance.instance_name,
        data: enhancedFormData
      });

      await axios.post('/api/form-submissions/create-submission', {
        submission_data: enhancedFormData,
        form_instance_id: modalFormInstance.id,
        student_intake_id: studentIntakeId
      });

      // Refresh the forms list
      const formsResponse = await axios.get(`/api/student-intake/${studentIntakeId}/forms`);
      console.log('Refreshed forms list:', formsResponse.data);
      setSubmittedForms(formsResponse.data || []);

      const formLabel = modalFormInstance.instance_name || modalFormInstance.master_form_name;
      setShowAddFormModal(false);
      setModalFormInstance(null);
      setModalFormDefinition(null);
      setModalFormData({});
      setSaveFeedback({
        variant: 'success',
        title: 'Submitted',
        message: `${formLabel} was submitted successfully.`
      });

    } catch (error) {
      console.error('Failed to submit form from modal:', error);

              if (error.response?.status === 409 && error.response.data.can_overwrite) {
        // Duplicate submission - offer overwrite option
        const shouldOverwrite = window.confirm(
          `${error.response.data.error}\n\n⚠️  Warning: This will overwrite the existing submission with your current data.\n\nDo you want to continue and overwrite?`
        );

        if (shouldOverwrite) {
          // Overwrite the existing submission
          try {
            await axios.put(`/api/form-submissions/overwrite-submission/${error.response.data.existing_submission_id}`, {
              submission_data: enhancedFormData,
              student_intake_id: studentIntakeId
            });

            const owLabel = modalFormInstance.instance_name || modalFormInstance.master_form_name;
            const formsResponse = await axios.get(`/api/student-intake/${studentIntakeId}/forms`);
            setSubmittedForms(formsResponse.data || []);

            setShowAddFormModal(false);
            setModalFormInstance(null);
            setModalFormDefinition(null);
            setModalFormData({});
            setSaveFeedback({
              variant: 'success',
              title: 'Updated',
              message: `${owLabel} was updated successfully.`
            });
          } catch (overwriteError) {
            console.error('Failed to overwrite submission:', overwriteError);
            setSaveFeedback({
              variant: 'error',
              title: 'Overwrite failed',
              message: overwriteError.response?.data?.error || 'Could not overwrite the existing submission.'
            });
          }
        }
        // If user cancels, stay in modal with form data intact
      } else if (error.response?.status === 409) {
        // Duplicate submission without overwrite option
        setSaveFeedback({
          variant: 'error',
          title: 'Cannot submit',
          message: error.response.data.error || 'This form cannot be submitted right now.'
        });
      } else {
        setSaveFeedback({
          variant: 'error',
          title: 'Submit failed',
          message: error.response?.data?.error || 'Failed to submit form'
        });
      }
    } finally {
      setSavingModal(false);
    }
  };

  /**
   * Has the parent actually typed something into the add-form modal?
   *
   * This screen has no autosave and no draft on purpose - the submit is one atomic request, so
   * there is no half-saved state and no save race of the kind the combined packet has to recover
   * from. The cost of that choice is that everything on screen lives only in this component, so
   * closing the modal or leaving the page throws it away with nothing to restore from. The form
   * routed here is 83 fields, 61 of them required, which is too much to lose silently.
   *
   * Signature fields store an object, so emptiness is checked by shape rather than by string.
   */
  const hasUnsavedModalAnswers = React.useMemo(() => {
    if (!showAddFormModal) return false;
    return Object.entries(modalFormData || {}).some(([key, value]) => {
      if (key.startsWith('_')) return false; // _submissionMetadata and friends are not answers
      if (value == null) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') return Object.keys(value).length > 0;
      return String(value).trim() !== '';
    });
  }, [showAddFormModal, modalFormData]);

  /**
   * The browser's own "leave site?" prompt, for a tab close or a back button. React Router
   * navigations inside the app do not fire this, which is why the close button asks separately.
   */
  React.useEffect(() => {
    if (!hasUnsavedModalAnswers) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedModalAnswers]);

  // Handle closing the modal
  const closeAddFormModal = ({ force = false } = {}) => {
    if (!force && hasUnsavedModalAnswers) {
      const discard = window.confirm(
        'Discard your answers?\n\nThis form has not been submitted yet, and nothing you have ' +
        'typed is saved. If you close now you will have to fill it in again.'
      );
      if (!discard) return;
    }
    setShowAddFormModal(false);
    setModalFormInstance(null);
    setModalFormDefinition(null);
    setModalFormData({});
  };

  // Go back to form list
  const backToFormList = () => {
    setSelectedForm(null);
    setCurrentFormDefinition(null);
    setCurrentFormData({});
    setCurrentSubmissionId(null);
    setError('');
    setSaveFeedback(null);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="edit-student-form">
      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={() => navigate('/parent/dashboard')}
          style={{
            background: 'none',
            border: 'none',
            color: '#007bff',
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: '0'
          }}
        >
          ← Back to Dashboard
        </button>
      </div>

      <div style={{
        background: '#fff3cd',
        color: '#856404',
        padding: '0.75rem 1.5rem',
        borderRadius: '4px',
        marginBottom: '1rem',
        border: '1px solid #ffeaa7'
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
          Edit Forms - {studentIntake ? `${studentIntake.first_name} ${studentIntake.last_name}` : 'Student'}
        </div>
        <div>Select a form below to edit its data individually.</div>
      </div>

      {error && (
        <div style={{
          background: '#f8d7da',
          color: '#721c24',
          padding: '0.75rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {!selectedForm ? (
        // Form Selection View
        <div>
          <h3>Submitted Forms</h3>
          {submittedForms.length > 0 ? (
            <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
              {submittedForms.map((form, index) => (
                <div key={index} style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '1rem',
                  background: '#f9f9f9'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                    <div>
                      <h4 style={{ margin: '0 0 0.5rem 0' }}>
                        {form.instance_name || form.master_form_name}
                      </h4>
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        Submitted: {new Date(form.submitted_at).toLocaleDateString()}
                        {form.form_data && ` • ${Object.keys(form.form_data).length} fields`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem', maxWidth: 'min(100%, 280px)' }}>
                      <button
                        type="button"
                        onClick={() => selectForm(form)}
                        style={{
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Edit Form
                      </button>
                      {parentEditsLocked ? (
                        <p
                          style={{
                            margin: 0,
                            fontSize: '0.8rem',
                            lineHeight: 1.4,
                            color: '#6c757d',
                            textAlign: 'left',
                            width: '100%'
                          }}
                        >
                          {parentEditsLockedNote}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No submitted forms found for this student.</p>
          )}

          <h3>Add New Form</h3>
          <div style={{ display: 'grid', gap: '1rem' }}>
            {availableForms
              .filter(form => !submittedForms.some(submitted => submitted.form_instance_id === form.id))
              .map((form, index) => (
                <div key={index} style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '1rem',
                  background: '#fff'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4 style={{ margin: '0 0 0.5rem 0' }}>
                        {form.instance_name || form.master_form_name}
                      </h4>
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        {form.instance_description || 'No description available'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!loading) {
                          openAddFormModal(form);
                        }
                      }}
                      disabled={loading}
                      style={{
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '4px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.6 : 1
                      }}
                    >
                      Add Form
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : (
        // Individual Form Editing View
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <button
              onClick={() => backToFormList()}
              style={{
                background: 'none',
                border: '1px solid #007bff',
                color: '#007bff',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              ← Back to Forms
            </button>
            <h3 style={{ margin: 0 }}>
              Editing: {selectedForm.instance_name || selectedForm.master_form_name}
            </h3>
          </div>

          {currentFormDefinition && (
            <FormRenderer
              formData={currentFormDefinition}
              formValues={currentFormData}
              onChange={setCurrentFormData}
              userRole="parent"
              hideFormYear
            />
          )}

          <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
            <button
              onClick={() => saveEditedForm(currentFormData)}
              disabled={saving}
              style={{
                background: '#007bff',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '4px',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Add Form Modal */}
      {showAddFormModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0 }}>
                Add {modalFormInstance?.instance_name || modalFormInstance?.master_form_name}
              </h3>
              <button
                onClick={() => closeAddFormModal()}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '1.5rem' }}>
              {console.log('Rendering FormRenderer with:', { modalFormDefinition, modalFormData })}
              {modalFormDefinition && (
                <FormRenderer
                  formData={modalFormDefinition}
                  formValues={modalFormData}
                  onChange={setModalFormData}
                  userRole="parent"
                  hideFormYear
                />
              )}
              {!modalFormDefinition && <div>No form definition loaded</div>}

              <div style={{
                marginTop: '2rem',
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => closeAddFormModal()}
                  disabled={savingModal}
                  style={{
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '4px',
                    cursor: savingModal ? 'not-allowed' : 'pointer',
                    opacity: savingModal ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitModalForm}
                  disabled={savingModal}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '4px',
                    cursor: savingModal ? 'not-allowed' : 'pointer',
                    opacity: savingModal ? 0.6 : 1
                  }}
                >
                  {savingModal ? 'Submitting...' : 'Submit Form'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <SuccessPopup
        show={!!saveFeedback}
        onClose={() => setSaveFeedback(null)}
        variant={saveFeedback?.variant || 'success'}
        title={saveFeedback?.title}
        message={saveFeedback?.message || ''}
        autoCloseDelay={saveFeedback?.variant === 'error' ? 0 : 5000}
      />
    </div>
  );
};

export default EditStudentForm;
